import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import Login from './Login';

import './modal-fix.css';
import './mobile-fix.css';

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
    description: '',
    status: 'pending'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [draggedProduct, setDraggedProduct] = useState(null);
  const [touchStartY, setTouchStartY] = useState(0); // eslint-disable-line no-unused-vars
  const [sales, setSales] = useState([]); // eslint-disable-line no-unused-vars
  const [sellQuantity, setSellQuantity] = useState({});
  const [showSalesHistory, setShowSalesHistory] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [monthlySales, setMonthlySales] = useState({});
  const [expandedMonths, setExpandedMonths] = useState(new Set([new Date().toISOString().slice(0, 7)]));
  const [lastCheckedMonth, setLastCheckedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // MOBILE TOUCH STATES
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartTime, setDragStartTime] = useState(0);
  
  // BULLETPROOF PAGINATION & PERFORMANCE
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const ITEMS_PER_PAGE = 50;
  const [hasMore, setHasMore] = useState(true);
  const [cachedProducts, setCachedProducts] = useState(new Map());
  const [lastFetch, setLastFetch] = useState(0);
  const CACHE_DURATION = 30000; // 30 seconds

  // Custom Toast Notification System
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    // Sanitize message to prevent code injection - escape HTML and remove scripts
    const sanitizedMessage = String(message)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
    const toast = { id, message: sanitizedMessage, type };
    
    setToasts(prev => [...prev, toast]);
    
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // BULLETPROOF IMMEDIATE DATABASE FETCHING
  const fetchProducts = async (page = 1, search = '', category = 'all', forceRefresh = false, showLoading = false) => {
    if (!user) return;
    
    const cacheKey = `${page}-${search}-${category}`;
    const now = Date.now();
    
    // Skip cache for immediate updates when forceRefresh is true
    if (!forceRefresh && cachedProducts.has(cacheKey) && (now - lastFetch) < CACHE_DURATION) {
      const cached = cachedProducts.get(cacheKey);
      setProducts(cached.data);
      setTotalProducts(cached.total);
      setHasMore(cached.hasMore);
      return;
    }
    
    try {
      // Only show loading when explicitly requested
      if (showLoading) {
        setSearchLoading(true);
      }
      
      // IMMEDIATE DATABASE QUERY - No delays
      let query = supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE - 1);
      
      // Add search filter
      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }
      
      // Add category filter
      if (category !== 'all') {
        query = query.eq('category', category);
      }
      
      // EXECUTE QUERY IMMEDIATELY
      const { data, error, count } = await query;
      
      if (error) {
        console.error('Products fetch error:', error);
        showToast('Error loading products', 'error');
        return;
      }
      
      const hasMoreData = count > page * ITEMS_PER_PAGE;
      
      // Update cache immediately
      const cacheData = {
        data: data || [],
        total: count || 0,
        hasMore: hasMoreData,
        timestamp: now
      };
      
      setCachedProducts(prev => new Map(prev.set(cacheKey, cacheData)));
      setLastFetch(now);
      
      // UPDATE UI IMMEDIATELY
      setProducts(data || []);
      setTotalProducts(count || 0);
      setHasMore(hasMoreData);
      
      console.log(`⚡ INSTANT DB FETCH - Page ${page}: ${data?.length || 0} products (Total: ${count})`);
      
    } catch (error) {
      console.error('Products fetch exception:', error);
      showToast('Network error loading products', 'error');
      setProducts([]);
    } finally {
      if (showLoading) {
        setSearchLoading(false);
      }
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!user) return;
    
    try {
      // IMMEDIATE DATABASE QUERY
      const { data, error } = await supabase
        .from('categories')
        .select('name')
        .eq('user_id', user.id)
        .order('name');
      
      if (error) {
        console.error('Categories fetch error:', error);
        showToast('Error loading categories', 'error');
        return;
      }
      
      // UPDATE UI IMMEDIATELY
      setCategories(data?.map(cat => cat.name) || []);
      console.log('⚡ INSTANT Categories DB FETCH:', data?.length || 0, 'categories');
    } catch (error) {
      console.error('Categories fetch exception:', error);
      showToast('Network error loading categories', 'error');
      setCategories([]);
    }
  };

  const fetchSales = async () => {
    if (!user) return;
    
    try {
      // IMMEDIATE DATABASE QUERY
      const { data, error } = await supabase
        .from('sales')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Sales fetch error:', error);
        showToast('Error loading sales', 'error');
        return;
      }
      
      // UPDATE UI IMMEDIATELY
      setSales(data || []);
      
      // Group sales by month IMMEDIATELY
      const grouped = {};
      (data || []).forEach(sale => {
        const monthKey = new Date(sale.created_at).toISOString().slice(0, 7);
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push(sale);
      });
      setMonthlySales(grouped);
      
      console.log('⚡ INSTANT Sales DB FETCH:', data?.length || 0, 'sales');
      
    } catch (error) {
      console.error('Sales fetch exception:', error);
      showToast('Network error loading sales', 'error');
      setSales([]);
      setMonthlySales({});
    }
  };

  // AUTHENTICATION SETUP
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Utility function for getting current month
  const getCurrentMonth = () => {
    return new Date().toISOString().slice(0, 7);
  };

  // Auto-switch months when new month starts
  useEffect(() => {
    const currentMonth = getCurrentMonth();
    if (currentMonth !== lastCheckedMonth) {
      // New month started - close previous month and open current month
      setExpandedMonths(new Set([currentMonth]));
      setLastCheckedMonth(currentMonth);
      console.log(`📅 New month started: ${currentMonth}`);
    }
  }, [lastCheckedMonth]);

  // Check for month change every minute
  useEffect(() => {
    const checkMonthChange = () => {
      const currentMonth = getCurrentMonth();
      if (currentMonth !== lastCheckedMonth) {
        setExpandedMonths(new Set([currentMonth]));
        setLastCheckedMonth(currentMonth);
        console.log(`📅 Month changed to: ${currentMonth}`);
      }
    };

    const interval = setInterval(checkMonthChange, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [lastCheckedMonth]);

  // DATA LOADING WHEN USER CHANGES
  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setCachedProducts(new Map());
      fetchProducts(1, searchTerm, selectedCategory, true);
      fetchCategories();
      fetchSales();
    } else {
      setProducts([]);
      setCategories([]);
      setSales([]);
      setCurrentPage(1);
      setTotalProducts(0);
    }
  }, [user]); // Only depend on user
  
  // INSTANT SEARCH WITH IMMEDIATE DATABASE QUERIES
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (user) {
        setCurrentPage(1);
        setCachedProducts(new Map()); // Clear cache on search
        // IMMEDIATE DATABASE SEARCH - show loading only for search
        fetchProducts(1, searchTerm, selectedCategory, true, searchTerm.length > 0);
      }
    }, 300); // Reduced to 300ms for faster response
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory, user]);

  // CROSS-DEVICE SYNC
  useEffect(() => {
    if (!user) return;

    const channel = supabase.channel(`sync-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products', filter: `user_id=eq.${user.id}` }, () => {
        setCachedProducts(new Map());
        fetchProducts(currentPage, searchTerm, selectedCategory, true);
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'categories', filter: `user_id=eq.${user.id}` }, () => {
        fetchCategories();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sales', filter: `user_id=eq.${user.id}` }, () => {
        fetchSales();
      })
      .subscribe();

    // Removed aggressive 1000ms polling - real-time subscriptions handle updates
    // Only sync on visibility change, not continuously

    // Real-time updates handle everything instantly, no polling needed

    // INSTANT REFRESH ON WINDOW FOCUS - NO DELAYS
    const handleFocus = () => {
      console.log('⚡ WINDOW FOCUS - Immediate DB refresh');
      setCachedProducts(new Map()); // Clear cache for immediate refresh
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      fetchCategories();
      fetchSales();
    };
    window.addEventListener('focus', handleFocus);
    window.addEventListener('visibilitychange', handleFocus);

    // NETWORK RECONNECTION HANDLER - IMMEDIATE SYNC
    const handleOnline = () => {
      console.log('🌐 NETWORK RECONNECTED - IMMEDIATE FULL DB SYNC');
      setCachedProducts(new Map());
      // Force immediate refresh of all data
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      fetchCategories();
      fetchSales();
    };
    window.addEventListener('online', handleOnline);
    
    // Enhanced visibility change handler for better cross-device sync
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('⚡ Tab became visible - Force sync all data');
        setCachedProducts(new Map());
        fetchProducts(currentPage, searchTerm, selectedCategory, true);
        fetchCategories();
        fetchSales();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // syncInterval removed - no longer needed
      supabase.removeChannel(channel);
    };
  }, [user, currentPage, searchTerm, selectedCategory, lastFetch]); // Fixed dependencies

  const handleLogin = () => {
    // Handled by auth state change
  };

  const handleLogout = async () => {
    try {
      console.log('Starting logout process...');
      setLoading(true);
      
      // Try to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Supabase logout error:', error);
        // Even if there's an error, clear local state
        clearLocalState();
        showToast('Logged out (with warning): ' + error.message, 'error');
      } else {
        console.log('Supabase logout successful');
        clearLocalState();
        showToast('Logged out successfully', 'success');
      }
    } catch (error) {
      console.error('Logout exception:', error);
      // Force logout by clearing local state
      clearLocalState();
      showToast('Force logged out due to error', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const clearLocalState = () => {
    console.log('Clearing local state...');
    setUser(null);
    setProducts([]);
    setCategories([]);
    setSales([]);
    setMonthlySales({});
    setCachedProducts(new Map());
    setCurrentPage(1);
    setTotalProducts(0);
    setSearchTerm('');
    setSelectedCategory('all');
    setEditingProduct(null);
    setShowModal(false);
    setShowCategoryModal(false);
    setShowSalesHistory(false);
    setSellQuantity({});
    console.log('Local state cleared');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 gradient-smooth flex items-center justify-center">
        <div className="text-center smooth-all">
          <div className="loading-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4 gpu-accelerated"></div>
          <p className="text-gray-600 smooth-opacity">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // BULLETPROOF CRUD OPERATIONS
  const addProduct = async () => {
    // Enhanced input validation
    if (!newProduct.name?.trim() || !newProduct.category || !newProduct.price || !newProduct.stock) {
      showToast('Please fill all required fields', 'error');
      return;
    }
    
    const price = parseFloat(newProduct.price);
    const stock = parseInt(newProduct.stock);
    
    // Validate data types and ranges
    if (isNaN(price) || price <= 0) {
      showToast('Price must be a positive number', 'error');
      return;
    }
    
    if (isNaN(stock) || stock < 0) {
      showToast('Stock must be a non-negative number', 'error');
      return;
    }
    
    if (newProduct.name.trim().length > 100) {
      showToast('Product name must be less than 100 characters', 'error');
      return;
    }
    
    if (newProduct.description && newProduct.description.length > 500) {
      showToast('Description must be less than 500 characters', 'error');
      return;
    }
    
    try {
      const product = {
        name: newProduct.name.trim(),
        category: newProduct.category,
        price: price,
        stock: stock,
        description: newProduct.description?.trim() || '',
        status: 'pending',
        user_id: user.id
      };
      
      console.log('Adding product with status:', product.status);
      
      // DATABASE FIRST - NO OPTIMISTIC UPDATES
      const { data, error } = await supabase.from('products').insert([product]).select();
      
      console.log('Product saved to DB:', data);
      
      if (error) {
        showToast('Error adding product: ' + error.message, 'error');
        return;
      }
      
      // VERIFY DATA WAS SAVED
      const { data: verify } = await supabase.from('products').select('*').eq('id', data[0].id).single();
      if (!verify) {
        showToast('Data verification failed - please try again', 'error');
        return;
      }
      
      setNewProduct({ name: '', category: '', price: '', stock: '', description: '', status: 'pending' });
      setShowModal(false);
      showToast(`Added ${product.name}`, 'success');
      
      // FORCE REFRESH TO ENSURE SYNC
      setCachedProducts(new Map());
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      
    } catch (error) {
      console.error('Error adding product:', error);
      showToast('Error adding product: ' + error.message, 'error');
    }
  };

  const updateProduct = async () => {
    // Enhanced input validation
    if (!editingProduct.name?.trim() || !editingProduct.category || !editingProduct.price || !editingProduct.stock) {
      showToast('Please fill all required fields', 'error');
      return;
    }
    
    const price = parseFloat(editingProduct.price);
    const stock = parseInt(editingProduct.stock);
    
    // Validate data types and ranges
    if (isNaN(price) || price <= 0) {
      showToast('Price must be a positive number', 'error');
      return;
    }
    
    if (isNaN(stock) || stock < 0) {
      showToast('Stock must be a non-negative number', 'error');
      return;
    }
    
    if (editingProduct.name.trim().length > 100) {
      showToast('Product name must be less than 100 characters', 'error');
      return;
    }
    
    if (editingProduct.description && editingProduct.description.length > 500) {
      showToast('Description must be less than 500 characters', 'error');
      return;
    }
    
    try {
      
      // DATABASE FIRST - NO OPTIMISTIC UPDATES
      const { error } = await supabase
        .from('products')
        .update({
          name: editingProduct.name.trim(),
          category: editingProduct.category,
          price: price,
          stock: stock,
          description: editingProduct.description?.trim() || '',
          updated_at: new Date().toISOString()
        })
        .eq('id', editingProduct.id)
        .eq('user_id', user.id);
      
      if (error) {
        showToast('Error updating product: ' + error.message, 'error');
        return;
      }
      
      // VERIFY UPDATE WAS SAVED
      const { data: verify } = await supabase.from('products').select('*').eq('id', editingProduct.id).single();
      if (!verify) {
        showToast('Update verification failed - please try again', 'error');
        return;
      }
      
      setEditingProduct(null);
      setShowModal(false);
      showToast(`Updated ${editingProduct.name}`, 'success');
      
      // FORCE REFRESH TO ENSURE SYNC
      setCachedProducts(new Map());
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      
    } catch (error) {
      console.error('Error updating product:', error);
      showToast('Error updating product: ' + error.message, 'error');
    }
  };

  const deleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        // INSTANT UI UPDATE
        const productToDelete = products.find(p => p.id === id);
        setProducts(prev => prev.filter(p => p.id !== id));
        showToast(`Deleted ${productToDelete?.name}`, 'success');
        
        // IMMEDIATE DATABASE DELETE
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);
        
        if (error) {
          setProducts(prev => [...prev, productToDelete].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
          showToast('Error deleting product: ' + error.message, 'error');
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        showToast('Error deleting product: ' + error.message, 'error');
      }
    }
  };

  // Check if product can be edited (within 10 minutes of creation)
  const canEditProduct = (product) => {
    const createdAt = new Date(product.created_at);
    const now = new Date();
    const timeDiff = (now - createdAt) / (1000 * 60); // difference in minutes
    return timeDiff <= 10;
  };

  const editProduct = (product) => {
    if (!canEditProduct(product)) {
      showToast('Cannot edit item after 10 minutes of creation', 'error');
      return;
    }
    setEditingProduct({ ...product });
    setShowModal(true);
  };

  const addCategory = async () => {
    if (!newCategory.trim()) {
      showToast('Please enter a category name', 'error');
      return;
    }
    
    if (categories.includes(newCategory.trim())) {
      showToast('Category already exists', 'error');
      return;
    }
    
    try {
      const categoryName = newCategory.trim();
      
      // ⚡ INSTANT UI UPDATE (optimistic)
      setCategories(prev => [...prev, categoryName].sort());
      setNewCategory('');
      setShowCategoryModal(false);
      showToast(`Added category: ${categoryName}`, 'success');
      
      // Database insert in background
      supabase
        .from('categories')
        .insert([{ name: categoryName, user_id: user.id }])
        .then(({ error }) => {
          if (error) {
            // Revert on error
            setCategories(prev => prev.filter(cat => cat !== categoryName));
            showToast('Error adding category: ' + error.message, 'error');
          }
        });
    } catch (error) {
      console.error('Error adding category:', error);
      showToast('Error adding category: ' + error.message, 'error');
    }
  };

  const deleteCategory = async (categoryToDelete) => {
    if (window.confirm(`Are you sure you want to delete the category "${categoryToDelete}"? All products in this category will be moved to "Uncategorized".`)) {
      try {
        // INSTANT UI UPDATES
        const originalCategories = [...categories];
        const originalProducts = [...products];
        
        // Update UI immediately
        if (!categories.includes('Uncategorized')) {
          setCategories(prev => [...prev, 'Uncategorized'].sort());
        }
        setCategories(prev => prev.filter(cat => cat !== categoryToDelete));
        setProducts(prev => prev.map(p => 
          p.category === categoryToDelete ? { ...p, category: 'Uncategorized', updated_at: new Date().toISOString() } : p
        ));
        showToast(`Deleted category: ${categoryToDelete}`, 'success');
        
        // IMMEDIATE DATABASE OPERATIONS
        // Add Uncategorized if it doesn't exist
        if (!originalCategories.includes('Uncategorized')) {
          await supabase.from('categories').insert([{ name: 'Uncategorized', user_id: user.id }]);
        }
        
        // Update all products in this category to Uncategorized
        const updateResult = await supabase
          .from('products')
          .update({ category: 'Uncategorized', updated_at: new Date().toISOString() })
          .eq('category', categoryToDelete)
          .eq('user_id', user.id);
        
        // Delete the category
        const deleteResult = await supabase
          .from('categories')
          .delete()
          .eq('name', categoryToDelete)
          .eq('user_id', user.id);
        
        if (updateResult.error || deleteResult.error) {
          // Revert UI changes on error
          setCategories(originalCategories);
          setProducts(originalProducts);
          showToast('Error deleting category: ' + (updateResult.error?.message || deleteResult.error?.message), 'error');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        showToast('Error deleting category: ' + error.message, 'error');
      }
    }
  };



  // COMPLETE DRAG AND DROP SYSTEM
  const handleDragStart = (e, productId) => {
    e.dataTransfer.setData('productId', productId.toString());
    setDraggedProduct(productId);
    e.currentTarget.style.opacity = '0.7';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedProduct(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drop-zone-highlight');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drop-zone-highlight');
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drop-zone-highlight');
    const productId = e.dataTransfer.getData('productId');
    if (!productId) return;
    
    // Block moving from pending status - only approval app can approve
    const product = products.find(p => p.id === parseInt(productId));
    if (product && product.status === 'pending') {
      showToast('Use the approval app to approve pending items', 'error');
      return;
    }
    
    // Instant update without await
    updateProductStatus(parseInt(productId), newStatus);
  };

  // MOBILE-FIRST TOUCH SUPPORT
  const handleTouchStart = (e, productId) => {
    const startTime = Date.now();
    setDragStartTime(startTime);
    setDraggedProduct(productId);
    setTouchStartY(e.touches[0].clientY);
    
    // Delay drag start to avoid conflicts with scrolling
    setTimeout(() => {
      if (draggedProduct === productId && Date.now() - startTime > 150) {
        setIsDragging(true);
        e.currentTarget.style.opacity = '0.7';
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.zIndex = '1000';
        e.currentTarget.style.transition = 'all 0.2s ease';
        
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
    }, 150);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !draggedProduct) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    document.querySelectorAll('.drop-zone-highlight').forEach(el => {
      el.classList.remove('drop-zone-highlight');
    });
    
    const dropZone = element?.closest('[data-drop-zone]');
    if (dropZone) {
      dropZone.classList.add('drop-zone-highlight');
      if (navigator.vibrate) {
        navigator.vibrate(25);
      }
    }
  };

  const handleTouchEnd = async (e) => {
    const touchDuration = Date.now() - dragStartTime;
    
    if (!isDragging || touchDuration < 150) {
      // Short tap - reset and return
      setDraggedProduct(null);
      setIsDragging(false);
      return;
    }
    
    if (!draggedProduct) return;
    
    const touch = e.changedTouches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    
    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.zIndex = 'auto';
    e.currentTarget.style.transition = 'all 0.2s ease';
    
    document.querySelectorAll('.drop-zone-highlight').forEach(el => {
      el.classList.remove('drop-zone-highlight');
    });
    
    const dropZone = element?.closest('[data-drop-zone]');
    if (dropZone && draggedProduct) {
      const newStatus = dropZone.getAttribute('data-drop-zone');
      

      
      // Block moving from pending status - only approval app can approve
      const product = products.find(p => p.id === draggedProduct);
      if (product && product.status === 'pending') {
        showToast('Use the approval app to approve pending items', 'error');
        setDraggedProduct(null);
        setIsDragging(false);
        return;
      }
      
      updateProductStatus(draggedProduct, newStatus);
      
      if (navigator.vibrate) {
        navigator.vibrate([50, 50, 50]);
      }
    }
    
    setDraggedProduct(null);
    setIsDragging(false);
  };

  const updateProductStatus = async (productId, newStatus) => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) return;
      
      // Handle sell functionality
      if (newStatus === 'sell') {
        await handleSell(productId);
        return;
      }
      
      const finalStatus = newStatus;
      const updatedAt = new Date().toISOString();
      
      // ⚡ INSTANT UI UPDATE (optimistic)
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === productId ? { ...p, status: finalStatus, updated_at: updatedAt } : p
        )
      );
      
      showToast(`${product.name} moved to ${finalStatus.replace('-', ' ')}`, 'success');
      
      // Database update in background
      supabase
        .from('products')
        .update({ 
          status: finalStatus,
          updated_at: updatedAt
        })
        .eq('id', productId)
        .eq('user_id', user.id)
        .then(({ error }) => {
          if (error) {
            // Revert on error
            setProducts(prevProducts => 
              prevProducts.map(p => 
                p.id === productId ? { ...p, status: product.status } : p
              )
            );
            showToast('Error updating product status: ' + error.message, 'error');
          }
        });
      
    } catch (error) {
      console.error('Error updating product status:', error);
      showToast('Error updating product status: ' + error.message, 'error');
    }
  };

  const handleSell = async (productId, quantity = 1) => {
    const product = products.find(p => p.id === productId);
    const inputQty = sellQuantity[productId];
    const sellQty = inputQty && inputQty !== '' && inputQty !== 0 ? parseInt(inputQty) : 1;
    
    if (!product || isNaN(sellQty) || sellQty <= 0 || sellQty > product.stock) {
      showToast('Invalid quantity or insufficient stock', 'error');
      return;
    }

    try {
      // Use the atomic sale processing function to prevent race conditions
      const { data, error } = await supabase.rpc('process_sale_immediate', {
        p_product_id: productId,
        p_quantity: sellQty,
        p_user_id: user.id
      });
      
      if (error) {
        showToast('Error processing sale: ' + error.message, 'error');
        return;
      }
      
      const result = data;
      if (!result.success) {
        showToast('Sale failed: ' + result.error, 'error');
        return;
      }
      
      const totalAmount = sellQty * product.price;
      setSellQuantity(prev => ({ ...prev, [productId]: 1 }));
      showToast(`Sold ${sellQty} ${product.name}(s) for ₹${totalAmount.toLocaleString('en-IN')}`);
      
      // FORCE REFRESH ALL DATA
      setCachedProducts(new Map());
      fetchProducts(currentPage, searchTerm, selectedCategory, true);
      fetchSales();
      
    } catch (error) {
      console.error('Error processing sale:', error);
      showToast('Error processing sale: ' + error.message, 'error');
    }
  };

  // UTILITY FUNCTIONS
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusProducts = (status) => {
    return filteredProducts.filter(product => product.status === status);
  };

  const getTotalValue = () => {
    return products.reduce((total, product) => total + (product.price * product.stock), 0).toLocaleString('en-IN');
  };

  const getTotalItems = () => {
    return products.reduce((total, product) => total + product.stock, 0);
  };

  const toggleMonth = (monthKey) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(monthKey)) {
      newExpanded.delete(monthKey);
    } else {
      newExpanded.add(monthKey);
    }
    setExpandedMonths(newExpanded);
  };

  const getMonthName = (monthKey) => {
    const date = new Date(monthKey + '-01');
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 gradient-smooth gpu-accelerated">
      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast ${toast.type} show`} onClick={() => removeToast(toast.id)}>
            <div className="flex items-center justify-between">
              <span className="font-medium">{toast.message}</span>
              <button className="ml-4 text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile-Optimized Header */}
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-xl gradient-smooth">
        <div className="container mx-auto px-3 md:px-6 py-3 md:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between header-mobile">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                <span className="text-yellow-300">भारत</span> Inventory Pro
              </h1>
              <p className="text-amber-100 text-sm md:text-base">Complete Inventory Management Solution</p>
            </div>
            <div className="flex flex-col lg:flex-row gap-3 md:gap-4 items-center">
              <div className="flex flex-col md:flex-row flex-wrap gap-3 items-center w-full md:w-auto">
                <div className="relative w-full md:w-auto">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-3 rounded-xl border-0 focus:ring-3 focus:ring-amber-300 w-full md:w-64 text-gray-800 font-medium"
                  />
                  <svg className="w-5 h-5 absolute left-3 top-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 rounded-xl border-0 focus:ring-3 focus:ring-amber-300 bg-white text-gray-800 font-medium"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap gap-2 w-full md:w-auto justify-center md:justify-start mobile-buttons">
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className="bg-white text-amber-700 px-4 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors flex items-center justify-center text-base min-h-[48px] flex-1 sm:flex-initial"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Category
                </button>
                <button
                  onClick={() => {
                    setEditingProduct(null);
                    setShowModal(true);
                  }}
                  className="bg-white text-amber-700 px-4 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors flex items-center justify-center text-base min-h-[48px] flex-1 sm:flex-initial"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Product
                </button>
                <button
                  onClick={() => setShowSalesHistory(true)}
                  className="bg-white text-green-700 px-4 py-3 rounded-xl font-semibold hover:bg-green-50 transition-colors flex items-center justify-center text-base min-h-[48px] flex-1 sm:flex-initial"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Sales
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Logout button clicked');
                    handleLogout();
                  }}
                  disabled={loading}
                  className="bg-red-500 text-white px-4 py-3 rounded-xl font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-base min-h-[48px] w-full sm:w-auto"
                  style={{ touchAction: 'manipulation' }}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {loading ? 'Logging out...' : 'Logout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 md:px-6 py-3 md:py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-6">
          <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 border-t-4 border-amber-500 card-smooth stagger-item gpu-accelerated">
            <div className="flex items-center">
              <div className="p-2 md:p-4 rounded-2xl bg-amber-100 text-amber-600 mr-3 md:mr-6">
                <svg className="w-6 h-6 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Inventory Value</p>
                <p className="text-xl md:text-3xl font-bold text-gray-800 mt-1">₹{getTotalValue()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 border-t-4 border-green-500 card-smooth stagger-item gpu-accelerated">
            <div className="flex items-center">
              <div className="p-2 md:p-4 rounded-2xl bg-green-100 text-green-600 mr-3 md:mr-6">
                <svg className="w-6 h-6 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Items in Stock</p>
                <p className="text-xl md:text-3xl font-bold text-gray-800 mt-1">{getTotalItems()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8 border-t-4 border-blue-500 card-smooth stagger-item gpu-accelerated">
            <div className="flex items-center">
              <div className="p-2 md:p-4 rounded-2xl bg-blue-100 text-blue-600 mr-3 md:mr-6">
                <svg className="w-6 h-6 md:w-10 md:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <p className="text-gray-500 text-xs md:text-sm font-medium">Total Products</p>
                <p className="text-xl md:text-3xl font-bold text-gray-800 mt-1">{products.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Loading Indicator - Only show during user searches */}
        {searchLoading && (
          <div className="flex justify-center items-center py-4">
            <div className="loading-spin rounded-full h-6 w-6 border-b-2 border-amber-600 mr-2"></div>
            <span className="text-gray-600">Searching...</span>
          </div>
        )}
        
        {/* Pagination Info */}
        <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
          <span>Showing {products.length} of {totalProducts} products</span>
          <span>Page {currentPage}</span>
        </div>

        {/* Mobile-Optimized Kanban Board */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 lg:gap-6 items-start">
          {/* Pending Column - READ ONLY */}
          <div className="bg-gray-100 rounded-2xl p-3 lg:p-6 h-fit kanban-mobile">
            <div className="bg-yellow-500 text-white p-3 lg:p-4 rounded-xl flex items-center justify-between mb-3 lg:mb-6">
              <h2 className="font-bold text-lg lg:text-xl">Pending Approval</h2>
              <span className="bg-white text-yellow-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                {getStatusProducts('pending').length}
              </span>
            </div>
            <div className="bg-orange-100 border border-orange-300 rounded-lg p-2 mb-4">
              <p className="text-orange-700 text-xs font-medium text-center">
                ⚠️ Use approval app to approve these items
              </p>
            </div>
            <div className="space-y-4">
              {getStatusProducts('pending').map(product => (
                <div 
                  key={product.id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, product.id)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, product.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="bg-white rounded-xl p-3 lg:p-4 shadow-lg border-l-4 border-yellow-500 cursor-grab active:cursor-grabbing transition-all duration-200 touch-manipulation select-none"
                  style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{product.name}</h3>
                    <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <span className="inline-block bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full mb-2">{product.category}</span>
                  <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <p className="font-bold text-gray-800">₹{product.price.toLocaleString('en-IN')}</p>
                    <span className="text-orange-600 text-sm bg-orange-50 px-3 py-2 rounded min-h-[44px] min-w-[60px] flex items-center justify-center">
                      Awaiting Approval
                    </span>
                  </div>
                </div>
              ))}
              {getStatusProducts('pending').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No pending products</p>
                </div>
              )}
            </div>
          </div>

          {/* In Stock Column */}
          <div 
            className="bg-gray-100 rounded-2xl p-6 h-fit" 
            onDragOver={handleDragOver} 
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 'in-stock')} 
            data-drop-zone="in-stock"
          >
            <div className="bg-green-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
              <h2 className="font-bold text-xl">In Stock</h2>
              <span className="bg-white text-green-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                {getStatusProducts('in-stock').length}
              </span>
            </div>
            <div className="space-y-4">
              {getStatusProducts('in-stock').map(product => (
                <div 
                  key={product.id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, product.id)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, product.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="bg-white rounded-xl p-4 shadow-lg border-l-4 border-green-500 cursor-grab active:cursor-grabbing transition-all duration-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{product.name}</h3>
                    <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <span className="inline-block bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full mb-2">{product.category}</span>
                  <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-gray-800">₹{product.price.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-green-600">Stock: {product.stock}</p>
                    </div>
                    {canEditProduct(product) ? (
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          editProduct(product);
                        }}
                        className="text-blue-600 text-sm bg-blue-50 px-3 py-2 rounded min-h-[44px] min-w-[60px] flex items-center justify-center touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Edit
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs bg-gray-100 px-3 py-2 rounded min-h-[44px] min-w-[60px] flex items-center justify-center">
                        Locked
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {getStatusProducts('in-stock').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No products in stock</p>
                </div>
              )}
            </div>
          </div>

          {/* Sell Items Column */}
          <div 
            className="bg-gray-100 rounded-2xl p-6 h-fit" 
            onDragOver={handleDragOver} 
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 'sell')} 
            data-drop-zone="sell"
          >
            <div className="bg-blue-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
              <h2 className="font-bold text-xl">Sell Items</h2>
              <span className="bg-white text-blue-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                {products.filter(p => p.stock > 0 && p.status !== 'pending').length}
              </span>
            </div>
            <div className="space-y-4">
              {products.filter(p => p.stock > 0 && p.status !== 'pending').map(product => (
                <div 
                  key={product.id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, product.id)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => handleTouchStart(e, product.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  className="bg-white rounded-xl p-4 shadow-lg border-l-4 border-blue-500 cursor-grab active:cursor-grabbing transition-all duration-200 touch-manipulation select-none"
                  style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-800">{product.name}</h3>
                    <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <span className="inline-block bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full mb-2">{product.category}</span>
                  <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-bold text-gray-800">₹{product.price.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-blue-600">Stock: {product.stock}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-2">
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-gray-600">Qty:</span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const current = sellQuantity[product.id] || 1;
                            if (current > 1) {
                              setSellQuantity(prev => ({ ...prev, [product.id]: current - 1 }));
                            }
                          }}
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center text-lg font-bold"
                        >
                          -
                        </button>
                        <span className="w-12 text-center font-bold text-lg">{sellQuantity[product.id] || 1}</span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const current = sellQuantity[product.id] || 1;
                            if (current < product.stock) {
                              setSellQuantity(prev => ({ ...prev, [product.id]: current + 1 }));
                            }
                          }}
                          className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center text-lg font-bold"
                        >
                          +
                        </button>
                      </div>
                      <button 
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const qty = sellQuantity[product.id] || 1;
                          handleSell(product.id, qty);
                        }}
                        className="bg-green-500 hover:bg-green-600 active:bg-green-700 text-white px-4 py-3 rounded-lg text-base font-semibold min-h-[48px] flex-1 sm:flex-initial sm:min-w-[80px] flex items-center justify-center transition-colors"
                        style={{ 
                          WebkitTapHighlightColor: 'rgba(34, 197, 94, 0.3)',
                          touchAction: 'manipulation',
                          userSelect: 'none'
                        }}
                      >
                        Sell
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {products.filter(p => p.stock > 0 && p.status !== 'pending').length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No items available for sale</p>
                </div>
              )}
            </div>
          </div>

          {/* Low Stock & Out of Stock Column */}
          <div className="space-y-6 h-fit">
            {/* Low Stock */}
            <div 
              className="bg-gray-100 rounded-2xl p-6 h-fit" 
              onDragOver={handleDragOver} 
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'low-stock')} 
              data-drop-zone="low-stock"
            >
              <div className="bg-orange-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
                <h2 className="font-bold text-lg">Low Stock</h2>
                <span className="bg-white text-orange-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                  {getStatusProducts('low-stock').length}
                </span>
              </div>
              <div className="space-y-4">
                {getStatusProducts('low-stock').map(product => (
                  <div 
                    key={product.id} 
                    draggable
                    onDragStart={(e) => handleDragStart(e, product.id)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, product.id)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    className="bg-white rounded-xl p-3 shadow-lg border-l-4 border-orange-500 cursor-grab active:cursor-grabbing transition-all duration-200"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-gray-800 text-sm">{product.name}</h3>
                      <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-gray-800 text-sm">₹{product.price.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-orange-600">Stock: {product.stock}</p>
                      </div>
                      {canEditProduct(product) ? (
                        <button onClick={() => editProduct(product)} className="text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded">Edit</button>
                      ) : (
                        <span className="text-gray-400 text-xs bg-gray-100 px-2 py-1 rounded">Locked</span>
                      )}
                    </div>
                  </div>
                ))}
                {getStatusProducts('low-stock').length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <p>No low stock products</p>
                  </div>
                )}
              </div>
            </div>

            {/* Out of Stock */}
            <div 
              className="bg-gray-100 rounded-2xl p-6 h-fit" 
              onDragOver={handleDragOver} 
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'out-of-stock')} 
              data-drop-zone="out-of-stock"
            >
              <div className="bg-red-500 text-white p-4 rounded-xl flex items-center justify-between mb-6">
                <h2 className="font-bold text-lg">Out of Stock</h2>
                <span className="bg-white text-red-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                  {getStatusProducts('out-of-stock').length}
                </span>
              </div>
              <div className="space-y-4">
                {getStatusProducts('out-of-stock').map(product => (
                  <div 
                    key={product.id} 
                    draggable
                    onDragStart={(e) => handleDragStart(e, product.id)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, product.id)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    className="bg-white rounded-xl p-3 shadow-lg border-l-4 border-red-500 cursor-grab active:cursor-grabbing transition-all duration-200"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-bold text-gray-800 text-sm">{product.name}</h3>
                      <button onClick={() => deleteProduct(product.id)} className="text-red-500 hover:text-red-700">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-gray-800 text-sm">₹{product.price.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-red-600">Stock: {product.stock}</p>
                      </div>
                      {canEditProduct(product) ? (
                        <button onClick={() => editProduct(product)} className="text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded">Edit</button>
                      ) : (
                        <span className="text-gray-400 text-xs bg-gray-100 px-2 py-1 rounded">Locked</span>
                      )}
                    </div>
                  </div>
                ))}
                {getStatusProducts('out-of-stock').length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <p>No out of stock products</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Pagination Controls */}
        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => {
              if (currentPage > 1) {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
                fetchProducts(newPage, searchTerm, selectedCategory, false, true);
              }
            }}
            disabled={currentPage === 1 || searchLoading}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-700 transition-colors"
          >
            Previous
          </button>
          
          <span className="px-4 py-2 bg-gray-100 rounded-lg font-medium">
            Page {currentPage}
          </span>
          
          <button
            onClick={() => {
              if (hasMore) {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
                fetchProducts(newPage, searchTerm, selectedCategory, false, true);
              }
            }}
            disabled={!hasMore || searchLoading}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-700 transition-colors"
          >
            Next
          </button>
        </div>
      </div>

      {/* Modal for Add/Edit Product */}
      {showModal && (
        <div className="modal-backdrop p-4">
          <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingProduct(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                <input
                  type="text"
                  value={editingProduct ? editingProduct.name : newProduct.name}
                  onChange={(e) => editingProduct 
                    ? setEditingProduct({...editingProduct, name: e.target.value})
                    : setNewProduct({...newProduct, name: e.target.value})
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800"
                  placeholder="Enter product name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                <select
                  value={editingProduct ? editingProduct.category : newProduct.category}
                  onChange={(e) => editingProduct 
                    ? setEditingProduct({...editingProduct, category: e.target.value})
                    : setNewProduct({...newProduct, category: e.target.value})
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800"
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editingProduct ? editingProduct.price : newProduct.price}
                    onChange={(e) => editingProduct 
                      ? setEditingProduct({...editingProduct, price: e.target.value})
                      : setNewProduct({...newProduct, price: e.target.value})
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800"
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stock *</label>
                  <input
                    type="number"
                    min="0"
                    value={editingProduct ? editingProduct.stock : newProduct.stock}
                    onChange={(e) => editingProduct 
                      ? setEditingProduct({...editingProduct, stock: e.target.value})
                      : setNewProduct({...newProduct, stock: e.target.value})
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800"
                    placeholder="0"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={editingProduct ? editingProduct.description : newProduct.description}
                  onChange={(e) => editingProduct 
                    ? setEditingProduct({...editingProduct, description: e.target.value})
                    : setNewProduct({...newProduct, description: e.target.value})
                  }
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800"
                  placeholder="Enter product description"
                ></textarea>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-8">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingProduct(null);
                }}
                className="px-6 py-3 text-gray-700 bg-gray-200 rounded-xl hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={editingProduct ? updateProduct : addProduct}
                className="px-6 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-medium"
              >
                {editingProduct ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Add Category */}
      {showCategoryModal && (
        <div className="modal-backdrop p-4">
          <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Manage Categories</h2>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add New Category</label>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent text-gray-800"
                    placeholder="Enter category name"
                  />
                  <button
                    onClick={addCategory}
                    className="px-4 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-medium"
                  >
                    Add
                  </button>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-3">Existing Categories</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {categories.map(category => (
                    <div key={category} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                      <span className="font-medium text-gray-800">{category}</span>
                      <button
                        onClick={() => deleteCategory(category)}
                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-8">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-6 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sales History Modal */}
      {showSalesHistory && (
        <div className="modal-backdrop p-4">
          <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-4xl w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Sales History</h2>
                <p className="text-gray-600 mt-1">Complete record of all your sales transactions</p>
              </div>
              <button
                onClick={() => setShowSalesHistory(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {/* Sales Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg mr-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Total Revenue</p>
                      <p className="text-xl font-bold text-green-800">
                        ₹{sales.reduce((total, sale) => total + parseFloat(sale.total_amount), 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Sales</p>
                      <p className="text-xl font-bold text-blue-800">{sales.length}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg mr-3">
                      <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Items Sold</p>
                      <p className="text-xl font-bold text-purple-800">
                        {sales.reduce((total, sale) => total + sale.quantity_sold, 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sales by Month */}
              <div className="max-h-96 overflow-y-auto">
                {Object.keys(monthlySales).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p>No sales recorded yet</p>
                    <p className="text-sm text-gray-400 mt-1">Start selling products to see your sales history here</p>
                  </div>
                ) : (
                  Object.keys(monthlySales).sort().reverse().map(monthKey => {
                    const monthSales = monthlySales[monthKey];
                    const isCurrentMonth = monthKey === getCurrentMonth();
                    const isExpanded = isCurrentMonth || expandedMonths.has(monthKey);
                    
                    return (
                      <div key={monthKey} className="mb-6">
                        {/* Month Header */}
                        <div 
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => toggleMonth(monthKey)}
                        >
                          <div className="flex items-center">
                            <svg className={`w-4 h-4 mr-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                            <h3 className="text-lg font-semibold text-gray-800">{getMonthName(monthKey)}</h3>
                          </div>
                          <span className="text-sm text-gray-600">{monthSales.length} sales</span>
                        </div>
                        
                        {/* Sales Table */}
                        {isExpanded && (
                          <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {monthSales.map((sale, index) => (
                                  <tr key={sale.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <div>
                                        <p className="font-medium">{new Date(sale.created_at).toLocaleDateString('en-IN')}</p>
                                        <p className="text-gray-500 text-xs">{new Date(sale.created_at).toLocaleTimeString('en-IN')}</p>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{sale.product_name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {sale.quantity_sold}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">₹{parseFloat(sale.sale_price).toLocaleString('en-IN')}</td>
                                    <td className="px-4 py-3 text-sm font-semibold text-green-600">₹{parseFloat(sale.total_amount).toLocaleString('en-IN')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            
            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowSalesHistory(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-300">© 2025 भारत Inventory Pro. Made with ❤️ for Indian Businesses.</p>
          <p className="text-amber-300 mt-2 font-medium">
            Developed by 
            <a 
              href="https://subhojit-paul.pages.dev" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-yellow-300 transition-colors cursor-pointer underline"
            >
              Subhojit
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;